import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { InfoIcon, X } from "lucide-react";
import storage from "utils/Storage";
import { getUID } from 'utils/Helper';
import { useMyProfileQuery, useLevelQuery } from "hooks/useUser";
import "./VipClub.css";
import VipPopUp1 from "./VipPopUp1";

// ─── Styled Components from original code ─────────────────────────────────────
const Container = styled.div`
  color: white;
  padding: 20px;
`;

const LevelsScrollContainer = styled.div`
  display: flex;
  gap: 14px;
  overflow-x: auto;
  padding-bottom: 12px;
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
`;

const CarouselContainerVip = styled.div`
  margin-right: 170px;
  @media (max-width: 454px) {
    margin-right: 90px !important;
  }
  @media (max-width: 284px) {
    margin-right: 50px !important;
  }
`;

const CardVip = styled.div`
  color: white;
  border-radius: 10px;
  padding: 20px;
  width: 100%;
  position: relative;
  cursor: pointer;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 150px;
  padding: 10px;

  @media (max-width: 854px) {
    display: flex;
    justify-content: space-around;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: #2f3136;
  border-radius: 4px;
  overflow: hidden;
`;

const Progress = styled.div`
  width: ${(props) => props.progress}%;
  height: 100%;
  background-color: #5865f2;
  border-radius: 4px;
`;

const LevelIndicator = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  margin-bottom: 8px;
`;

const LevelCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: clamp(14px, 2vw, 18px);
  margin-right: 8px;
  text-transform: no-wrap;
`;

const StatsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  color: #72767d;
  font-size: 12px;
`;

const MainWrapper = styled.div`
  display: flex;
  height: 180px;
  gap: 10px;
  align-items: center;
  margin-top: 20px;
  position: relative;
`;

const InfoContainer = styled.div`
  flex: 1;
`;

const InfoHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 10px;
`;

const CustomModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: #17181b;
  color: white;
  width: 900px;
  border-radius: 8px;
  padding: 20px;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.div`
  margin: 0;
  font-size: 18px;
  font-weight: bold;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const ModalBody = styled.div`
  max-height: 80vh;
  overflow-y: auto;
  text-align: center;
`;

// ─── Tier theme config ────────────────────────────────────────────────────────
const tierTheme = {
  bronze: {
    gradient: "linear-gradient(135deg, #3d2b1f 0%, #1a1008 100%)",
    border: "#cd7f3240",
    glow: "rgba(205, 127, 50, 0.15)",
    text: "#cd7f32",
    badge: "/assets/bcimages/vipclub/badge-bronze.webp",
    bgCard: "rgba(205,127,50,0.08)",
  },
  silver: {
    gradient: "linear-gradient(135deg, #2a2f35 0%, #0f1117 100%)",
    border: "#c0c0c040",
    glow: "rgba(192, 192, 192, 0.15)",
    text: "#c0c0c0",
    badge: "/assets/bcimages/vipclub/badge-silver.webp",
    bgCard: "rgba(192,192,192,0.08)",
  },
  gold: {
    gradient: "linear-gradient(135deg, #3d3000 0%, #1a1200 100%)",
    border: "#ffd70040",
    glow: "rgba(255, 215, 0, 0.15)",
    text: "#ffd700",
    badge: "/assets/bcimages/vipclub/badge-gold.webp",
    bgCard: "rgba(255,215,0,0.08)",
  },
  platinum: {
    gradient: "linear-gradient(135deg, #2a1f3d 0%, #100a1a 100%)",
    border: "#9370db40",
    glow: "rgba(147, 112, 219, 0.18)",
    text: "#9370db",
    badge: "/assets/bcimages/vipclub/badge-platinum.webp",
    bgCard: "rgba(147,112,219,0.08)",
  },
  diamond: {
    gradient: "linear-gradient(135deg, #2f1a2b 0%, #120811 100%)",
    border: "#dc8abb40",
    glow: "rgba(220, 138, 187, 0.18)",
    text: "#dc8abb",
    badge: "/assets/bcimages/vipclub/badge-none.webp",
    bgCard: "rgba(220,138,187,0.08)",
  },
};

const getTierTheme = (tier) => tierTheme[tier] || tierTheme.bronze;

// ─── VIP Level Card ───────────────────────────────────────────────────────────
const VipLevelCard = ({ level, isCurrentLevel, isUnlocked }) => {
  const theme = getTierTheme(level.tier);
  return (
    <div
      className="vip-level-card"
      style={{
        background: isCurrentLevel ? theme.gradient : "rgba(255,255,255,0.02)",
        border: `1.5px solid ${isCurrentLevel ? theme.border.replace("40", "90") : "rgba(255,255,255,0.06)"}`,
        borderRadius: 16,
        padding: "20px 16px",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s ease",
        cursor: "default",
        flexShrink: 0,
        width: 180,
        boxShadow: isCurrentLevel ? `0 0 30px ${theme.glow}` : "none",
      }}
    >
      {isCurrentLevel && (
        <div style={{
          position: "absolute", top: 8, right: 8,
          background: theme.text + "22", border: `1px solid ${theme.text}55`,
          color: theme.text, fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 20, textTransform: "uppercase", letterSpacing: 1
        }}>
          Current
        </div>
      )}

      {/* Badge icon */}
      <div style={{ textAlign: "center", marginBottom: 12 }}>
        <div style={{
          width: 52, height: 52, borderRadius: "50%",
          background: theme.bgCard,
          border: `2px solid ${theme.border}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto", fontSize: 22, fontWeight: 900, color: theme.text,
          boxShadow: isCurrentLevel ? `0 0 15px ${theme.glow}` : "none",
        }}>
          {level.level_number}
        </div>
      </div>

      <div style={{ textAlign: "center" }}>
        <div style={{ color: "white", fontWeight: 700, fontSize: 15, marginBottom: 2 }}>{level.level_name}</div>
        <div style={{ color: theme.text, fontSize: 11, fontWeight: 600, textTransform: "capitalize", marginBottom: 10 }}>{level.tier}</div>

        {/* Unlock condition */}
        <div style={{
          background: "rgba(255,255,255,0.05)", borderRadius: 8, padding: "6px 10px", marginBottom: 10,
          fontSize: 11, color: "rgba(255,255,255,0.5)"
        }}>
          <span style={{ color: theme.text, fontWeight: 700 }}>${Number(level.required_amount).toLocaleString()}</span>
          <br />
          <span>
            {level.condition_type === 'both'
              ? 'Bet & Deposit'
              : level.condition_type === 'deposit'
              ? 'Total Deposit'
              : 'Total Bet'}
          </span>
        </div>

        {/* Perks */}
        <div style={{ textAlign: "left" }}>
          {(level.perks || []).slice(0, 4).map((perk, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
              <span style={{ color: theme.text, fontSize: 10 }}>✓</span>
              <span style={{ color: isUnlocked ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.3)", fontSize: 11 }}>{perk}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Rotating Card Data ──────────────────────────────────────────────────────
const cardData = [
  {
    title: "Bronze Card",
    description: "Smart Player",
    subtitle: "You deserve the best",
    imageSrc: "/assets/images/vip-new/vip-1.webp",
  },
  {
    title: "Silver Card",
    description: "VIP Player",
    subtitle: "Enjoy exclusive benefits",
    imageSrc: "/assets/images/vip-new/vip-3.webp",
  },
  {
    title: "Gold Card",
    description: "Elite Player",
    subtitle: "Experience luxury gaming",
    imageSrc: "/assets/images/vip-new/vip-4.png",
  },
  {
    title: "Platinum Card",
    description: "Elite Player",
    subtitle: "Experience luxury gaming",
    imageSrc: "/assets/images/vip-new/vip-5.webp",
  },
  {
    title: "Diamond Card",
    description: "Elite Player",
    subtitle: "Experience luxury gaming",
    imageSrc: "/assets/images/vip-new/vip-2.webp",
  },
];

// ─── Main Component ───────────────────────────────────────────────────────────
const VipClub = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [progress, setProgress] = useState(0);
  const progressAnimationRef = useRef(null);

  const [userInfo, setUserInfo] = useState({});
  const [levelList, setLevelList] = useState([]);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [nextLevelAmount, setNextLevelAmount] = useState(0);
  const [currentLevelAmount, setCurrentLevelAmount] = useState(0);
  const [progressPercentage, setProgressPercentage] = useState(0);

  const [uid] = useState(getUID);

  const { data: myProfileData } = useMyProfileQuery(uid);
  const { data: levelData } = useLevelQuery();

  useEffect(() => {
    if (myProfileData?.data) {
      setUserInfo(myProfileData.data[0]);
    }
  }, [myProfileData]);

  useEffect(() => {
    if (levelData?.data) {
      setLevelList(levelData.data);
    }
  }, [levelData]);

  const animateProgress = (targetProgress) => {
    if (progressAnimationRef.current) {
      cancelAnimationFrame(progressAnimationRef.current);
    }
    let currentProgress = 0;
    const duration = 1000; // 1 second animation
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progressVal = Math.min(elapsed / duration, 1);

      currentProgress = progressVal * targetProgress;
      setProgress(currentProgress);

      if (progressVal < 1) {
        progressAnimationRef.current = requestAnimationFrame(animate);
      }
    };

    progressAnimationRef.current = requestAnimationFrame(animate);
  };

  function calculateLevelPercentage() {
    if (!userInfo || !levelList?.length) return;

    let myTotalBet = parseFloat(userInfo.total_bet_amount || 0);
    let myDeposit = parseFloat(userInfo.total_deposit_amount || userInfo.total_bet_amount || 0);
    let curLvl = 0;
    let curLvlAmt = 0;
    let nextLvlAmt = 0;

    for (let i = 0; i < levelList.length; i++) {
      const lvl = levelList[i];
      let isMet = false;
      if (lvl.condition_type === 'deposit') {
        isMet = myDeposit >= parseFloat(lvl.required_amount);
      } else if (lvl.condition_type === 'both') {
        isMet = myTotalBet >= parseFloat(lvl.required_amount) && myDeposit >= parseFloat(lvl.required_amount);
      } else {
        isMet = myTotalBet >= parseFloat(lvl.required_amount);
      }
      
      if (isMet) {
        curLvl = lvl.level_number;
        curLvlAmt = parseFloat(lvl.required_amount);
      } else {
        nextLvlAmt = parseFloat(lvl.required_amount);
        break;
      }
    }

    let percentage = 0;
    if (nextLvlAmt > curLvlAmt) {
      const nextLvlObj = levelList.find(l => parseFloat(l.required_amount) === nextLvlAmt);
      const condition = nextLvlObj?.condition_type || 'bet';
      if (condition === 'deposit') {
        percentage = ((myDeposit - curLvlAmt) / (nextLvlAmt - curLvlAmt)) * 100;
      } else if (condition === 'both') {
        const betPct = ((myTotalBet - curLvlAmt) / (nextLvlAmt - curLvlAmt)) * 100;
        const depPct = ((myDeposit - curLvlAmt) / (nextLvlAmt - curLvlAmt)) * 100;
        percentage = Math.min(betPct, depPct);
      } else {
        percentage = ((myTotalBet - curLvlAmt) / (nextLvlAmt - curLvlAmt)) * 100;
      }
    } else {
      percentage = 100;
    }

    setCurrentLevel(curLvl);
    setCurrentLevelAmount(curLvlAmt);
    setNextLevelAmount(nextLvlAmt);
    setProgressPercentage(percentage);
    
    animateProgress(Math.min(Math.max(percentage, 0), 100));
  }

  useEffect(() => {
    calculateLevelPercentage();
  }, [userInfo, levelList]);

  useEffect(() => {
    return () => {
      if (progressAnimationRef.current) {
        cancelAnimationFrame(progressAnimationRef.current);
      }
    };
  }, []);

  // Update active rotating card based on current tier
  useEffect(() => {
    if (levelList?.length && currentLevel > 0) {
      const curLvlObj = levelList.find(l => l.level_number === currentLevel);
      if (curLvlObj) {
        const tier = curLvlObj.tier?.toLowerCase();
        if (tier === 'bronze') setActiveIndex(0);
        else if (tier === 'silver') setActiveIndex(1);
        else if (tier === 'gold') setActiveIndex(2);
        else if (tier === 'platinum') setActiveIndex(3);
        else if (tier === 'diamond') setActiveIndex(4);
      }
    }
  }, [currentLevel, levelList]);

  const handleCardClick = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const activeCard = cardData[activeIndex];
  const activeLevels = levelList.filter(l => l.is_active !== false);
  const isLoggedIn = storage.getKey('logged') !== null;

  return (
    <Container>
      <div
        style={{
          borderRadius: "10px",
          background:
            "linear-gradient(rgba(93, 129, 172, 0.4) 0%, rgba(50, 55, 56, 0) 77.42%)",
        }}
      >
        <div className="vipclub-main-page">
          <MainWrapper>
            <CarouselContainerVip>
              <div className="image-logo-main-in">
                <img
                  className="image-logo-main-inner"
                  src={
                    currentLevel > 0
                      ? (levelList.find(l => l.level_number === currentLevel)?.tier?.toLowerCase() === 'bronze'
                        ? "/assets/bcimages/vipclub/badge-bronze.webp"
                        : levelList.find(l => l.level_number === currentLevel)?.tier?.toLowerCase() === 'silver'
                        ? "/assets/bcimages/vipclub/badge-silver.webp"
                        : levelList.find(l => l.level_number === currentLevel)?.tier?.toLowerCase() === 'gold'
                        ? "/assets/bcimages/vipclub/badge-gold.webp"
                        : levelList.find(l => l.level_number === currentLevel)?.tier?.toLowerCase() === 'platinum'
                        ? "/assets/bcimages/vipclub/badge-platinum.webp"
                        : levelList.find(l => l.level_number === currentLevel)?.tier?.toLowerCase() === 'diamond'
                        ? "/assets/bcimages/vipclub/badge-diamond.webp"
                        : "/assets/bcimages/vipclub/badge-none.webp")
                      : "/assets/bcimages/vipclub/badge-none.webp"
                  }
                  alt="VIP Badge"
                />
              </div>
            </CarouselContainerVip>

            <InfoContainer>
              <InfoHeader></InfoHeader>
              <ProgressBarContainer>
                <div>
                  <LevelIndicator>
                    <LevelCircle>
                      {'VIP ' + currentLevel}
                    </LevelCircle>
                  </LevelIndicator>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      fontSize: "clamp(10px, 2vw, 15px)",
                      marginBottom: "6px"
                    }}
                  >
                    <div>{"$ "}{currentLevelAmount.toLocaleString()}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <div>{"$ "}{nextLevelAmount > 0 ? nextLevelAmount.toLocaleString() : 'Max'}</div>
                      <InfoIcon className="info-icon-1" size={16} />
                    </div>
                  </div>
                  
                  <ProgressBar>
                    <Progress progress={progress} />
                  </ProgressBar>
                  <StatsContainer>
                    <div className="about-vip-level">
                      {isLoggedIn 
                        ? 'BET $' + parseFloat(userInfo?.total_bet_amount || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })
                        : 'Please login to track your wager progress'
                      }
                    </div>
                  </StatsContainer>
                </div>
              </ProgressBarContainer>
            </InfoContainer>
          </MainWrapper>
          <VipPopUp1 />
        </div>
      </div>

      {/* ── VIP Levels Tier Cards ── */}
      <div className="vip-svip-main" style={{ marginTop: "40px" }}>
        <div className="vip-svip-heading">Exclusive VIP Bonus</div>
        <div className="vip-svip-text-in">
          Discover the ultimate gaming experience and requirements for each level
        </div>
      </div>

      <div style={{ marginBottom: "32px", marginTop: "20px" }}>
        {activeLevels.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px", color: "rgba(255,255,255,0.3)", fontSize: 14 }}>
            VIP levels loading...
          </div>
        ) : (
          <LevelsScrollContainer>
            {activeLevels.map(level => {
              let isUnlocked = false;
              if (level.condition_type === 'deposit') {
                isUnlocked = parseFloat(userInfo?.total_deposit_amount || 0) >= parseFloat(level.required_amount);
              } else if (level.condition_type === 'both') {
                isUnlocked = parseFloat(userInfo?.total_bet_amount || 0) >= parseFloat(level.required_amount) && parseFloat(userInfo?.total_deposit_amount || 0) >= parseFloat(level.required_amount);
              } else {
                isUnlocked = parseFloat(userInfo?.total_bet_amount || 0) >= parseFloat(level.required_amount);
              }
              const isCurrentLevel = level.level_number === currentLevel;
              return (
                <VipLevelCard
                  key={level.id}
                  level={level}
                  isCurrentLevel={isCurrentLevel}
                  isUnlocked={isUnlocked}
                />
              );
            })}
          </LevelsScrollContainer>
        )}
      </div>

      {showModal && (
        <CustomModal onClick={handleCloseModal}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{activeCard?.title || "About VIP Cards"}</ModalTitle>
              <CloseButton onClick={handleCloseModal}>
                <X size={20} />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <img
                src={activeCard?.imageSrc || "/assets/images/vip-new/vip-1.webp"}
                style={{
                  height: "170px",
                  width: "292px",
                  objectFit: "contain",
                  marginBottom: "16px"
                }}
                alt="VIP Card"
              />
              <h3>{activeCard?.description}</h3>
              <p style={{ color: "rgba(255,255,255,0.6)", marginTop: "8px" }}>{activeCard?.subtitle}</p>
            </ModalBody>
          </ModalContent>
        </CustomModal>
      )}
    </Container>
  );
};

export default VipClub;
